// src/AnalyticsDashboard.js - Uber/Handy-style Analytics Dashboard
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supabase } from './supabaseClient';
import ProfileDialog from './ProfileDialog';

import { brand, ink, status, surface } from './designTokens';
const COLORS = [brand.gold, status.success, status.warning, status.danger, brand.gold];

export default function AnalyticsDashboard() {
  const [dateRange, setDateRange] = useState('30d'); // 7d, 30d, 90d, 1y, all
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    revenueChange: 0,
    totalBookings: 0,
    bookingsChange: 0,
    activeUsers: 0,
    usersChange: 0,
    avgBookingValue: 0,
    avgBookingValueChange: 0,
    completionRate: 0,
    completionRateChange: 0,
  });
  const [revenueData, setRevenueData] = useState([]);
  const [bookingTrends, setBookingTrends] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [serviceDistribution, setServiceDistribution] = useState([]);
  const [topStylists, setTopStylists] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [dialogPhone, setDialogPhone] = useState(null);

  const calculateDateRange = useCallback((range) => {
    const now = new Date();
    let startDate;
    
    switch (range) {
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '1y':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(0); // All time
    }
    
    return { startDate, endDate: now };
  }, []);

  const getWeek = useCallback((date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }, []);

  const isSameWeek = useCallback((date1, date2) => {
    return getWeek(date1) === getWeek(date2) && 
           date1.getFullYear() === date2.getFullYear();
  }, [getWeek]);

  const generateTimeSeries = useCallback((data, startDate, endDate, type) => {
    const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const interval = days > 90 ? 'week' : 'day';
    const series = [];
    
    let current = new Date(startDate);
    while (current <= endDate) {
      const dateKey = interval === 'week' 
        ? `${current.getFullYear()}-W${getWeek(current)}`
        : current.toISOString().split('T')[0];
      
      let value = 0;
      if (type === 'revenue') {
        value = data
          .filter(item => {
            const itemDate = new Date(item.created_at || item.booking_date);
            return interval === 'week'
              ? isSameWeek(itemDate, current)
              : itemDate.toISOString().split('T')[0] === dateKey;
          })
          .filter(item => item.status === 'Completed' || item.status === 'completed')
          .reduce((sum, item) => sum + parseFloat(item.amount || item.price || 0), 0);
      } else {
        value = data.filter(item => {
          const itemDate = new Date(item.created_at || item.booking_date);
          return interval === 'week'
            ? isSameWeek(itemDate, current)
            : itemDate.toISOString().split('T')[0] === dateKey;
        }).length;
      }
      
      series.push({ date: dateKey, value });
      
      if (interval === 'week') {
        current.setDate(current.getDate() + 7);
      } else {
        current.setDate(current.getDate() + 1);
      }
    }
    
    return series;
  }, [getWeek, isSameWeek]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = calculateDateRange(dateRange);
      const startISO = startDate.toISOString();
      const endISO = endDate.toISOString();

      // Fetch bookings
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('*')
        .gte('created_at', startISO)
        .lte('created_at', endISO);

      if (bookingsError) throw bookingsError;

      // Fetch profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('Profiles')
        .select('*')
        .gte('created_at', startISO)
        .lte('created_at', endISO);

      if (profilesError) throw profilesError;

      // Calculate metrics
      const completedBookings = bookings?.filter(b => b.status === 'Completed' || b.status === 'completed') || [];
      const totalRevenue = completedBookings.reduce((sum, b) => sum + parseFloat(b.amount || b.price || 0), 0);
      const totalBookings = bookings?.length || 0;
      const activeUsers = [...new Set(bookings?.map(b => b.client_id).filter(Boolean))].length;
      const avgBookingValue = completedBookings.length > 0 ? totalRevenue / completedBookings.length : 0;
      const completionRate = totalBookings > 0 ? (completedBookings.length / totalBookings) * 100 : 0;

      // Calculate previous period for comparison
      const prevStartDate = new Date(startDate.getTime() - (endDate.getTime() - startDate.getTime()));
      const prevStartISO = prevStartDate.toISOString();
      
      const { data: prevBookings } = await supabase
        .from('bookings')
        .select('*')
        .gte('created_at', prevStartISO)
        .lt('created_at', startISO);

      const prevCompleted = prevBookings?.filter(b => b.status === 'Completed' || b.status === 'completed') || [];
      const prevRevenue = prevCompleted.reduce((sum, b) => sum + parseFloat(b.amount || b.price || 0), 0);
      const prevBookingsCount = prevBookings?.length || 0;
      const prevActiveUsers = [...new Set(prevBookings?.map(b => b.client_id).filter(Boolean))].length;
      const prevAvgValue = prevCompleted.length > 0 ? prevRevenue / prevCompleted.length : 0;
      const prevCompletionRate = prevBookingsCount > 0 ? (prevCompleted.length / prevBookingsCount) * 100 : 0;

      // Calculate changes
      const revenueChange = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue) * 100 : 0;
      const bookingsChange = prevBookingsCount > 0 ? ((totalBookings - prevBookingsCount) / prevBookingsCount) * 100 : 0;
      const usersChange = prevActiveUsers > 0 ? ((activeUsers - prevActiveUsers) / prevActiveUsers) * 100 : 0;
      const avgValueChange = prevAvgValue > 0 ? ((avgBookingValue - prevAvgValue) / prevAvgValue) * 100 : 0;
      const completionChange = prevCompletionRate > 0 ? ((completionRate - prevCompletionRate) / prevCompletionRate) * 100 : 0;

      setMetrics({
        totalRevenue,
        revenueChange,
        totalBookings,
        bookingsChange,
        activeUsers,
        usersChange,
        avgBookingValue,
        avgBookingValueChange: avgValueChange,
        completionRate,
        completionRateChange: completionChange,
      });

      // Generate time series data
      const revenueByDay = generateTimeSeries(bookings || [], startDate, endDate, 'revenue');
      const bookingsByDay = generateTimeSeries(bookings || [], startDate, endDate, 'count');
      const usersByDay = generateTimeSeries(profiles || [], startDate, endDate, 'count');

      setRevenueData(revenueByDay);
      setBookingTrends(bookingsByDay);
      setUserGrowth(usersByDay);

      // Service distribution
      const serviceCounts = {};
      bookings?.forEach(b => {
        const service = b.service_title || 'Unknown';
        serviceCounts[service] = (serviceCounts[service] || 0) + 1;
      });
      setServiceDistribution(
        Object.entries(serviceCounts)
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 5)
      );

      // Top stylists
      // Key by stylist_profile_id (the real FK into Profiles) when present,
      // falling back to phone number only for legacy rows that predate it.
      const stylistEarnings = {};
      completedBookings.forEach(b => {
        const stylistKey = b.stylist_profile_id || b.stylists_phone_number;
        if (stylistKey) {
          stylistEarnings[stylistKey] = (stylistEarnings[stylistKey] || 0) + parseFloat(b.amount || b.price || 0);
        }
      });
      const topStylistsData = Object.entries(stylistEarnings)
        .map(([id, earnings]) => ({ id, earnings }))
        .sort((a, b) => b.earnings - a.earnings)
        .slice(0, 5);

      // Fetch stylist names — keys may be a Profiles.id (uuid) or a phone number
      const stylistKeys = topStylistsData.map(s => s.id);
      if (stylistKeys.length > 0) {
        const uuidKeys = stylistKeys.filter(k => k.includes('-'));
        const phoneKeys = stylistKeys.filter(k => !k.includes('-'));

        const stylistMap = new Map();
        if (uuidKeys.length > 0) {
          const { data: byId } = await supabase
            .from('Profiles')
            .select('id, first_name, last_name, phone_number')
            .in('id', uuidKeys);
          byId?.forEach(p => stylistMap.set(p.id, p));
        }
        if (phoneKeys.length > 0) {
          const { data: byPhone } = await supabase
            .from('Profiles')
            .select('id, first_name, last_name, phone_number')
            .in('phone_number', phoneKeys);
          byPhone?.forEach(p => stylistMap.set(p.phone_number, p));
        }

        setTopStylists(
          topStylistsData.map(s => {
            const profile = stylistMap.get(s.id);
            return {
              ...s,
              profileId: profile?.id || (s.id.includes('-') ? s.id : null),
              phoneNumber: profile?.phone_number || (s.id.includes('-') ? null : s.id),
              name: profile
                ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.phone_number
                : s.id,
            };
          })
        );
      }

      // Top clients
      const clientSpending = {};
      completedBookings.forEach(b => {
        const clientId = b.client_id || b.phone_number;
        if (clientId) {
          clientSpending[clientId] = (clientSpending[clientId] || 0) + parseFloat(b.amount || b.price || 0);
        }
      });
      const topClientsData = Object.entries(clientSpending)
        .map(([id, spending]) => ({ id, spending }))
        .sort((a, b) => b.spending - a.spending)
        .slice(0, 5);
      
      // Fetch client names — keys may be a Profiles.id (uuid) or a phone number
      const clientKeys = topClientsData.map(c => c.id);
      if (clientKeys.length > 0) {
        const uuidKeys = clientKeys.filter(k => k.includes('-'));
        const phoneKeys = clientKeys.filter(k => !k.includes('-'));

        const clientMap = new Map();
        if (uuidKeys.length > 0) {
          const { data: byId } = await supabase
            .from('Profiles')
            .select('id, first_name, last_name, phone_number')
            .in('id', uuidKeys);
          byId?.forEach(p => clientMap.set(p.id, p));
        }
        if (phoneKeys.length > 0) {
          const { data: byPhone } = await supabase
            .from('Profiles')
            .select('id, first_name, last_name, phone_number')
            .in('phone_number', phoneKeys);
          byPhone?.forEach(p => clientMap.set(p.phone_number, p));
        }

        setTopClients(
          topClientsData.map(c => {
            const profile = clientMap.get(c.id);
            return {
              ...c,
              profileId: profile?.id || (c.id.includes('-') ? c.id : null),
              phoneNumber: profile?.phone_number || (c.id.includes('-') ? null : c.id),
              name: profile
                ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.phone_number
                : c.id,
            };
          })
        );
      }

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, calculateDateRange, generateTimeSeries]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export analytics data');
  };

  const MetricCard = ({ title, value, change, format = 'number' }) => {
    const isPositive = change >= 0;
    const formattedValue = format === 'currency'
      ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : format === 'percentage'
      ? `${value.toFixed(1)}%`
      : value.toLocaleString();

    return (
      <Card elevation={0} sx={{ border: `1px solid ${surface.line}`, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="body2" sx={{ color: ink[500], mb: 1 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: ink[900] }}>
              {formattedValue}
            </Typography>
            {change !== undefined && (
              <Chip
                icon={isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
                label={`${isPositive ? '+' : ''}${change.toFixed(1)}%`}
                size="small"
                sx={{
                  bgcolor: isPositive ? status.successBg : status.dangerBg,
                  color: isPositive ? status.success : status.danger,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 4, bgcolor: surface.page, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: ink[900] }}>
          Analytics Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
              <MenuItem value="1y">Last Year</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchAnalytics} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ textTransform: 'none' }}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Revenue"
            value={metrics.totalRevenue}
            change={metrics.revenueChange}
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Bookings"
            value={metrics.totalBookings}
            change={metrics.bookingsChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Users"
            value={metrics.activeUsers}
            change={metrics.usersChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Booking Value"
            value={metrics.avgBookingValue}
            change={metrics.avgBookingValueChange}
            format="currency"
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${surface.line}`, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Revenue Trend
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Area type="monotone" dataKey="value" stroke={brand.gold} fill={brand.gold} fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${surface.line}`, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Service Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={serviceDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill={brand.gold}
                  dataKey="value"
                >
                  {serviceDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${surface.line}`, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Booking Trends
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={bookingTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Line type="monotone" dataKey="value" stroke={status.success} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${surface.line}`, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              User Growth
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={userGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Bar dataKey="value" fill={brand.gold} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Top Performers */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${surface.line}`, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Top Stylists
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {topStylists.map((stylist, idx) => (
                <Box
                  key={stylist.id}
                  onClick={() => stylist.phoneNumber && setDialogPhone(stylist.phoneNumber)}
                  sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    cursor: stylist.phoneNumber ? 'pointer' : 'default',
                    '&:hover': stylist.phoneNumber ? { opacity: 0.75 } : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label={`#${idx + 1}`} size="small" color="primary" />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {stylist.name}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: status.success }}>
                    ${stylist.earnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${surface.line}`, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Top Clients
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {topClients.map((client, idx) => (
                <Box
                  key={client.id}
                  onClick={() => client.phoneNumber && setDialogPhone(client.phoneNumber)}
                  sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    cursor: client.phoneNumber ? 'pointer' : 'default',
                    '&:hover': client.phoneNumber ? { opacity: 0.75 } : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label={`#${idx + 1}`} size="small" color="secondary" />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {client.name}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: brand.gold }}>
                    ${client.spending.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <ProfileDialog
        open={Boolean(dialogPhone)}
        phoneNumber={dialogPhone}
        onClose={() => setDialogPhone(null)}
      />
    </Box>
  );
}

